import * as XLSX from 'xlsx';

/**
 * Conversion des données d'un widget en lignes Excel — SOURCE UNIQUE.
 *
 * Il existait TROIS exporteurs : celui-ci (générique), une copie inline dans Widget.js et une
 * autre dans AnalyticsDashboard.js (menu 3 points d'un widget d'onglet custom). Les copies ne
 * traitaient explicitement que `abandon-table`, `sla-table` et `table` ; TOUT le reste tombait
 * dans une branche « graphique » ne gardant que Label/Value/Percentage. Les widgets tabulaires
 * dont le type n'était pas listé (`agent-status-table`, `ivr-stats-table`) n'ont aucune de ces
 * clés : leur export produisait autant de lignes que d'agents, mais toutes VIDES.
 *
 * Les trois points d'entrée (bouton du widget, menu 3 points, export d'onglet) passent
 * désormais tous par `buildWidgetRows`. Ne pas réintroduire de mise en forme locale ailleurs.
 *
 * On regarde désormais la DONNÉE plutôt que le type déclaré du widget. Un nouveau widget
 * tabulaire devient exportable sans qu'on ait à toucher ce fichier.
 */

/**
 * Durée en secondes → « hh:mm:ss », heures TOUJOURS présentes.
 *
 * ⚠️ Format volontairement DIFFÉRENT de l'affichage écran (TableWidget rend « m:ss »).
 * Ne pas « harmoniser » les deux : le web reste en m:ss par choix produit, l'export doit
 * être en hh:mm:ss.
 *
 * Pourquoi les heures même à zéro : Excel interprète nativement « 0:08 » comme h:mm, soit
 * 0 h 08. Une moyenne de sonnerie de 8 secondes exportée en « 0:08 » devenait donc 8 MINUTES
 * dès que la cellule était retapée, recollée avec conversion, ou ouverte par un tableur qui
 * auto-convertit. En « 00:00:08 », la lecture native d'Excel est la bonne : 8 secondes.
 *
 * Pas de plafond à 24 h : sur un cumul (temps de traitement total) les heures dépassent
 * légitimement 24, et « 27:14:03 » reste juste et lisible.
 */
export const formatDuration = (seconds) => {
    if (seconds === null || seconds === undefined || seconds === '') return '';
    const n = Number(seconds);
    if (!Number.isFinite(n) || n < 0) return '';
    const total = Math.floor(n);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

/**
 * Epoch (secondes) → heure d'horloge locale « 09:14:03 », TOUJOURS en 24 h et TOUJOURS
 * avec les secondes.
 * Utilisé pour les horodatages, JAMAIS pour les durées — voir CLOCK_KEYS ci-dessous.
 *
 * Pourquoi pas `toLocaleTimeString` : le format dépendait de la locale du poste. En anglais,
 * un log-in de 10 h 05 sortait « 10:05 AM » — texte qu'Excel ne reconnaît pas comme une heure —
 * et une déconnexion de 14 h 05 sortait « 02:05 PM », lue « 2 h 05 » par qui trie la colonne.
 * Sans les secondes, « 10:05 » restait par ailleurs indiscernable d'une durée mm:ss.
 * Un horodatage et une durée s'écrivent donc désormais tous deux hh:mm:ss, et Excel les
 * interprète nativement de la même façon.
 */
export const formatClock = (epoch, timeZone) => {
    if (epoch === null || epoch === undefined || epoch === '') return '';
    const n = Number(epoch);
    if (!Number.isFinite(n) || n <= 0) return '';
    const d = new Date(n * 1000);
    if (Number.isNaN(d.getTime())) return '';
    // `timeZone` (IANA, ex. « Europe/Lisbon ») = celui choisi dans la barre du rapport. Sans lui
    // l'heure suivait le POSTE qui exporte : deux superviseurs dans deux pays tiraient deux
    // fichiers différents de la même période, et l'écart passait inaperçu puisque rien n'affiche
    // le fuseau. Repli sur l'heure locale si absent ou invalide — le comportement d'avant.
    if (timeZone) {
        try {
            return new Intl.DateTimeFormat('en-GB', {
                timeZone, hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
            }).format(d);
        } catch (e) { /* fuseau inconnu : on retombe sur l'heure du poste */ }
    }
    const pad = (v) => String(v).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

/**
 * Quelles colonnes sont des DURÉES en secondes — donc à passer par formatDuration.
 *
 * ⚠️ Liste FERMÉE, volontairement pas une regex sur `*Time`.
 *
 * Une heuristique de nom attraperait `loginTime` / `logoutTime`, qui sont dans la MÊME ligne
 * `agent-status-table` que les durées mais sont des EPOCHS : `loginTime` valant 1754308440,
 * un formatage en durée sortirait « 487307:54:00 » au lieu de « 09:14:03 ». D'où les deux
 * ensembles disjoints ci-dessous, et le préfixe `dur_` distinct de `ts_` pour le neuf.
 *
 * CONVENTION pour tout NOUVEAU champ exposé par Data_Pipe :
 *   `dur_xxx` → durée en secondes  → hh:mm:ss
 *   `ts_xxx`  → epoch en secondes  → heure d'horloge
 * Détection automatique, rien à ajouter ici. Les clés ci-dessous sont l'historique
 * antérieur à la convention ; elles disparaîtront quand le backend déclarera ses formats
 * de colonnes par requête.
 */
const DURATION_KEYS = new Set([
    'totalLoggedOn', 'talkTime', 'maxTalkTime', 'readyTime', 'notReadyTime',
    'avgRingingTime', 'avgDurationSec', 'waitTime', 'alertTime',
    'averageWaitTime', 'totalWaitTime',
    // Liste d'appels (`all-calls-list`) : `waitTime` et `alertTime` étaient formatés, `duration`
    // — la durée de l'appel, même unité, même ligne — sortait en secondes brutes.
    'duration'
]);

/**
 * Métriques (widgets `type: 'metric'`) dont la valeur est une DURÉE en secondes.
 *
 * Le format d'affichage est déclaré dans la config du widget (`config.format === 'duration'`,
 * cf. Config/dashboardConfigs.js) et c'est ce signal qui prime. Cette liste est le filet pour
 * les widgets ajoutés au catalogue SANS bloc `config` : leur valeur est une durée côté backend
 * (`{ value: <secondes> }`) mais rien ne le déclare, et la cellule sortait en secondes brutes —
 * « 8 » pour une sonnerie moyenne de 8 s, à côté d'un « 00:00:08 » sur le tableau voisin.
 */
const DURATION_METRIC_QUERIES = new Set([
    'average-wait-time', 'avg-ringing-time', 'average-call-duration',
    'average-inbound-call-duration', 'average-outbound-call-duration',
    'average-handle-time', 'average-talk-time', 'avg-abandon-time',
    'max-abandon-time', 'max-talking-time', 'max-waiting-time'
]);

/** La valeur d'un widget métrique est-elle une durée en secondes ? */
export const isDurationMetric = (config = {}) => {
    // `longDuration` = même unité (des secondes), même rendu `hh:mm:ss` ici ; il ne se distingue
    // de `duration` qu'à l'écran, où il n'a pas de plafond à 24 h (cartes du canal mail).
    // L'oublier ferait sortir ces cartes en secondes brutes dans le fichier exporté.
    const fmt = config?.config?.format || config?.format;
    if (fmt === 'duration' || fmt === 'longDuration') return true;
    const queryId = config?.dataSource?.queryId || config?.queryId;
    return typeof queryId === 'string' && DURATION_METRIC_QUERIES.has(queryId);
};

/** Horodatages : ne JAMAIS les traiter comme des durées. */
const CLOCK_KEYS = new Set(['loginTime', 'logoutTime']);

/**
 * Objets imbriqués dont TOUTES les sous-clés sont des durées.
 * `statuses: { READY: 120, LUNCH: 60 }` est aplati par genericTableRows en colonnes
 * `statuses.READY` / `statuses.LUNCH` : ce sont des secondes passées dans chaque statut,
 * pas des compteurs.
 */
const DURATION_OBJECT_KEYS = new Set(['statuses']);

/**
 * La colonne `col` (éventuellement « parent.enfant ») est-elle une durée ?
 * @param {string} col nom de colonne
 * @param {object} row ligne source — `row.format === 'duration'` reste prioritaire
 */
export const isDurationColumn = (col, row = {}) => {
    if (typeof col !== 'string') return false;
    const [parent] = col.split('.');
    if (CLOCK_KEYS.has(col) || CLOCK_KEYS.has(parent)) return false;
    if (col.startsWith('ts_') || parent.startsWith('ts_')) return false;
    if (row && row.format === 'duration') return true;
    if (col.startsWith('dur_') || parent.startsWith('dur_')) return true;
    if (DURATION_OBJECT_KEYS.has(parent) && col !== parent) return true;
    return DURATION_KEYS.has(col);
};

/** La colonne est-elle un horodatage à rendre en heure d'horloge ? */
export const isClockColumn = (col) => {
    if (typeof col !== 'string') return false;
    const [parent] = col.split('.');
    return CLOCK_KEYS.has(col) || col.startsWith('ts_') || parent.startsWith('ts_');
};

/** Les lignes ressemblent-elles à une série de graphique (un libellé + une valeur) ? */
const isChartSeries = (rows) => {
    const first = rows[0];
    if (!first || typeof first !== 'object') return false;
    return (
        ('label' in first || 'name' in first || 'category' in first) &&
        ('value' in first || 'count' in first || 'y' in first)
    );
};

/**
 * Tableau générique : TOUTES les colonnes, et les objets imbriqués sont APLATIS
 * (ex. `statuses: { READY: 120, LUNCH: 60 }` → colonnes `statuses.READY`, `statuses.LUNCH`)
 * au lieu d'être jetés. Les colonnes sont collectées sur l'ENSEMBLE des lignes : un agent
 * peut avoir un statut qu'un autre n'a pas.
 */
const genericTableRows = (rows, timeZone) => {
    const columns = [];
    rows.forEach(row => {
        Object.keys(row || {}).forEach(key => {
            const value = row[key];
            if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
                Object.keys(value).forEach(sub => {
                    const col = `${key}.${sub}`;
                    if (!columns.includes(col)) columns.push(col);
                });
            } else if (!columns.includes(key)) {
                columns.push(key);
            }
        });
    });

    return rows.map(row => {
        const out = {};
        columns.forEach(col => {
            const [key, sub] = col.split('.');
            const raw = sub ? row?.[key]?.[sub] : row?.[key];
            if (raw === undefined || raw === null) {
                out[col] = '';
            } else if (Array.isArray(raw)) {
                out[col] = raw.join(', ');
            } else if (isDurationColumn(col, row)) {
                // Les durées sortaient ici en SECONDES BRUTES (« 127 ») faute de toute
                // conversion : c'est ce chemin que prennent ivr-stats-table et
                // agent-status-breakdown. Les colonnes et leur ordre sont inchangés,
                // seule la valeur de cellule est formatée.
                out[col] = formatDuration(raw);
            } else if (isClockColumn(col)) {
                out[col] = formatClock(raw, timeZone);
            } else {
                out[col] = raw;
            }
        });
        return out;
    });
};

/**
 * Données d'un widget → lignes prêtes pour une feuille Excel.
 * @param {*} data        donnée renvoyée par le backend (tableau, ou objet pour une métrique)
 * @param {object} config config du widget (type, title…)
 * @param {function} t    traducteur i18n (optionnel)
 * @returns {Array<object>} lignes ; tableau VIDE s'il n'y a rien d'exploitable
 */
export const buildWidgetRows = (data, config = {}, t = (k) => k, timeZone) => {
    if (data === null || data === undefined) return [];

    // Tableau des abandons journaliers — en-têtes métier traduits.
    if (config.type === 'abandon-table' && Array.isArray(data) && data.length > 0) {
        return data.map(row => ({
            [t('DAY') || 'Day']: row.day ?? '',
            [t('ABANDON_CALLS') || 'Abandon Calls']: row.abandonCalls ?? 0,
            '0-15s': row.count_0_15 ?? 0,
            '15-30s': row.count_15_30 ?? 0,
            '30-45s': row.count_30_45 ?? 0,
            '45-60s': row.count_45_60 ?? 0,
            '>60s': row.count_over_60 ?? 0,
            '% Abandon': row.pct_abandon != null ? `${row.pct_abandon}%` : '',
            '% < 15s': row.pct_abandon_lt_15 != null ? `${row.pct_abandon_lt_15}%` : '',
            '% < 30s': row.pct_abandon_lt_30 != null ? `${row.pct_abandon_lt_30}%` : '',
            [t('REDIRECTED_CALLS') || 'Redirected']: row.redirectedCalls ?? 0,
            [t('AVG_RINGING_TIME') || 'Avg Ringing']: formatDuration(row.avgRingingTime)
        }));
    }

    // Tableau SLA journalier.
    if (config.type === 'sla-table' && Array.isArray(data) && data.length > 0) {
        return data.map(row => ({
            [t('DAY') || 'Day']: row.day ?? '',
            [t('SERVED_CALLS') || 'Served Calls']: row.servedCalls ?? 0,
            [t('REDIRECTED_CALLS') || 'Redirected']: row.redirectedCalls ?? 0,
            '0-15s': row.count_0_15 ?? 0,
            '% < 15s': row.pct_lt_15 != null ? `${row.pct_lt_15}%` : '',
            '15-30s': row.count_15_30 ?? 0,
            '% < 30s': row.pct_lt_30 != null ? `${row.pct_lt_30}%` : '',
            '30-45s': row.count_30_45 ?? 0,
            '% < 45s': row.pct_lt_45 != null ? `${row.pct_lt_45}%` : '',
            '45-60s': row.count_45_60 ?? 0,
            '% < 60s': row.pct_lt_60 != null ? `${row.pct_lt_60}%` : '',
            '>60s': row.count_over_60 ?? 0
        }));
    }

    // Tableau statut agent — colonnes métier alignées sur l'affichage écran (dont Talk Time %,
    // que l'export générique noyait parmi les clés brutes queueId/agentId/statuses.* et laissait
    // non libellé). % suffixés.
    //
    // Les durées passent par le formateur PARTAGÉ hh:mm:ss. Avant, ce bloc avait son propre
    // formateur à géométrie variable — « m:ss » sous l'heure, « h:mm:ss » au-dessus, « 3j 2h05 »
    // au-delà du jour — donc trois formats dans un même classeur, dont un (« m:ss ») exposé au
    // contresens h:mm d'Excel. Un Not Ready de 3 jours s'exporte désormais « 74:05:12 » : moins
    // parlant à l'œil qu'« 3j 2h05 », mais homogène et exploitable en formule.
    if (config.type === 'agent-status-table' && Array.isArray(data) && data.length > 0) {
        const dur = formatDuration;
        const pct = (v) => {
            if (v === null || v === undefined || v === '') return '';
            const n = Number(v);
            if (!Number.isFinite(n)) return '';
            return `${n % 1 === 0 ? n.toFixed(0) : n.toFixed(1)}%`;
        };
        // Horodatages : formateur PARTAGÉ (hh:mm:ss, 24 h). La copie locale qui vivait ici
        // rendait « 10:05 AM » sur un poste en anglais.
        const time = (v) => formatClock(v, timeZone);
        return data.map(row => ({
            [t('QUEUE') || 'Queue']: row.queueName ?? '',
            [t('AGENT') || 'Agent']: row.agentName ?? '',
            [t('LOGIN') || 'Log-in']: time(row.loginTime),
            [t('LOGOUT') || 'Log-out']: time(row.logoutTime),
            [t('TOTAL_TIME_LOG_ON') || 'Total Time Log On']: dur(row.totalLoggedOn),
            [t('PRESENTED_CALLS') || 'Presented Calls']: row.presentedCalls ?? 0,
            [t('HANDLED_CALLS') || 'Handled Calls']: row.handledCalls ?? 0,
            [t('RING_ON_NO_ANSWER') || 'Ring on No Answer']: row.ringNoAnswer ?? 0,
            [t('ABANDON_CALLS') || 'Abandon Calls']: row.abandonCalls ?? 0,
            [t('TALK_TIME') || 'Talk Time']: dur(row.talkTime),
            [t('MAX_TALK_TIME') || 'Maximum Talk Time']: dur(row.maxTalkTime),
            [t('TALK_TIME_PCT') || 'Talk Time %']: pct(row.talkTimePct),
            [t('READY_TIME') || 'Ready Time']: dur(row.readyTime),
            [t('READY_TIME_PCT') || 'Ready Time %']: pct(row.readyTimePct),
            [t('NOT_READY_TIME') || 'Not Ready Time']: dur(row.notReadyTime),
            [t('NOT_READY_TIME_PCT') || 'Not Ready Time %']: pct(row.notReadyTimePct)
        }));
    }

    if (Array.isArray(data)) {
        if (data.length === 0) return [];

        if (isChartSeries(data)) {
            return data.map(item => ({
                Label: item.label ?? item.name ?? item.category ?? '',
                Value: item.value ?? item.count ?? item.y ?? 0,
                Percentage: item.percentage != null ? `${item.percentage}%` : ''
            }));
        }

        // Tableau transposé (KPI ou plage en lignes) : la colonne de libellé passe en tête,
        // et les durées sont formatées quand la ligne le demande (`format: 'duration'`).
        const first = data[0];
        if (first && typeof first === 'object' && ('kpi' in first || 'range' in first)) {
            const labelKey = 'kpi' in first ? 'kpi' : 'range';
            // Mêmes clés de métadonnées que TableWidget : ce qui est masqué à l'écran ne doit pas
            // réapparaître dans le xlsx. `agentName`/`flowName`/`queueName` étaient exportées en
            // colonnes vides sur les tableaux transposés, qui ne portent aucune de ces dimensions.
            const META_KEYS = ['format', 'agentName', 'flowName', 'queueName', 'agentId', 'flow', 'queue'];
            const dataKeys = Object.keys(first).filter(k => k !== labelKey && !META_KEYS.includes(k));
            return data.map(row => {
                const out = { [labelKey === 'kpi' ? 'KPI' : 'Range']: row[labelKey] ?? '' };
                dataKeys.forEach(k => {
                    const v = row[k];
                    // `row.format === 'duration'` reste le signal principal (posé par le
                    // backend) ; isDurationColumn ajoute le filet des clés historiques et de
                    // la convention dur_/ts_. Mêmes colonnes, même ordre qu'avant.
                    if (v == null) out[k] = '';
                    else if (isDurationColumn(k, row)) out[k] = formatDuration(v);
                    else if (isClockColumn(k)) out[k] = formatClock(v, timeZone);
                    else out[k] = v;
                });
                return out;
            });
        }

        return genericTableRows(data, timeZone);
    }

    // Widget métrique : une seule ligne.
    //
    // Les métriques de DURÉE (temps d'attente moyen, AHT, temps de conversation max…) renvoient
    // des secondes : elles sortaient telles quelles, « 69 », alors que la même donnée vaut
    // « 00:01:09 » sur le tableau KPI d'à côté. Colonnes et ordre inchangés, seule la valeur
    // des cellules Value / Max / Target est formatée — et uniquement pour les durées.
    if (typeof data === 'object') {
        const asValue = isDurationMetric(config)
            ? (v) => (v === null || v === undefined || v === '' ? '' : formatDuration(v))
            : (v) => v;
        return [{
            Metric: config.title || 'Metric',
            Value: asValue(data.value ?? 0),
            Trend: data.trend ?? 0,
            'Max Value': asValue(data.maxValue ?? ''),
            'Target Value': asValue(data.targetValue ?? '')
        }];
    }

    return [{ value: data }];
};

/** Nom de feuille valide : <= 31 caractères, sans []:*?/\, et UNIQUE dans le classeur. */
export const safeSheetName = (title, used = []) => {
    const base = (String(title || 'Widget').replace(/[\\/*?:[\]]/g, '_').trim() || 'Widget').slice(0, 31);
    let candidate = base;
    let i = 2;
    while (used.includes(candidate)) {
        const suffix = ` (${i})`;
        candidate = base.slice(0, 31 - suffix.length) + suffix;
        i += 1;
    }
    used.push(candidate);
    return candidate;
};

const dateStamp = () => new Date().toLocaleDateString('fr-FR', {
    year: 'numeric', month: '2-digit', day: '2-digit'
}).replace(/\//g, '-');

/**
 * Écrit un classeur : UNE FEUILLE PAR ENTRÉE. Les feuilles sans donnée sont ignorées —
 * un classeur ne doit pas contenir d'onglets vides qui laisseraient croire à une panne.
 * @param {Array<{name: string, rows: Array<object>}>} sheets
 * @param {string} filename
 * @returns {number} nombre de feuilles réellement écrites
 */
export const exportSheetsToExcel = (sheets, filename) => {
    const workbook = XLSX.utils.book_new();
    const used = [];
    let written = 0;

    (sheets || []).forEach(sheet => {
        if (!sheet?.rows?.length) return;
        const worksheet = XLSX.utils.json_to_sheet(sheet.rows);
        XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName(sheet.name, used));
        written += 1;
    });

    if (written === 0) return 0;

    XLSX.writeFile(workbook, filename);
    return written;
};

/** Export d'un widget seul (bouton du menu widget). */
export const exportWidgetToExcel = (data, title, config = {}, t, timeZone) => {
    const rows = buildWidgetRows(data, { ...config, title }, t, timeZone);
    if (rows.length === 0) {
        throw new Error('No data to export');
    }
    const safeTitle = (title || 'widget').replace(/[^a-zA-Z0-9_-]+/g, '_');
    exportSheetsToExcel([{ name: title, rows }], `${safeTitle}_${dateStamp()}.xlsx`);
};

/**
 * Export d'un ONGLET ENTIER : un classeur, une feuille par widget.
 * @param {Array<{title: string, data: *, config: object}>} widgets
 * @param {string} tabName
 * @param {string} scope
 * @param {function} t
 * @returns {number} feuilles écrites (0 = aucun widget n'avait de donnée)
 */
export const exportTabToExcel = (widgets, tabName, scope, t, timeZone) => {
    const sheets = (widgets || []).map(w => ({
        name: w.title,
        rows: buildWidgetRows(w.data, w.config, t, timeZone)
    }));

    const safeTab = (tabName || 'tab').replace(/[^a-zA-Z0-9_-]+/g, '_');
    const safeScope = (scope || 'report').replace(/[^a-zA-Z0-9_-]+/g, '_');
    return exportSheetsToExcel(sheets, `${safeScope}_${safeTab}_${dateStamp()}.xlsx`);
};
